import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Injector,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { debounceTime, filter, fromEvent, lastValueFrom } from 'rxjs';
import { Auth } from 'src/providers/auth/auth';
import { Communication, AttachmentPreview } from 'src/providers/communication/communication';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { Util } from 'src/shared/util/util';
import { ChatMessage, ChatSession, ConciergeQueriesService } from 'src/shared/services/concierge-queries.service';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { ChatDockService } from 'src/shared/services/chat-dock.service';
import { ChatLinkRedirectService } from 'src/shared/services/chat-link-redirect.service';
import { SharedLinkService } from 'src/shared/services/shared-link.service';
import { ChatAutosuggestService, ChatSuggestionResult } from 'src/shared/services/chat-autosuggest.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { Client } from 'src/providers/client/client';
import { Genome } from 'src/providers/genome/genome';
import { GeminiToolHandlerService } from 'src/shared/services/gemini-tool-handler.service';
import { GeminiLiveService } from 'src/shared/services/gemini-live.service';
import { LlmService } from 'src/shared/services/llm.service';

const ATTACH_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512"><path d="M216.08,192V335.85a40.08,40.08,0,0,0,80.15,0l.13-188.55a67.94,67.94,0,1,0-135.87,0V337.12a95.51,95.51,0,1,0,191,0V159.74" style="fill:none;stroke:#000;stroke-linecap:round;stroke-miterlimit:10;stroke-width:32px"/></svg>`;
const ATTACH_ICON_URI = `data:image/svg+xml,${encodeURIComponent(ATTACH_SVG)}`;

const ADD_BUTTON_SVG = `<svg width="569" height="569" viewBox="0 0 569 569" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_10581_153)"><mask id="mask0_10581_153" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="569" height="569"><path d="M568.889 0H0V568.889H568.889V0Z" fill="white"/></mask><g mask="url(#mask0_10581_153)"><path d="M540.443 256H312.891V28.4427C312.891 12.7448 300.146 0 284.443 0C268.745 0 256 12.7448 256 28.4427V256H28.4427C12.7448 256 0 268.745 0 284.443C0 300.146 12.7448 312.891 28.4427 312.891H256V540.443C256 556.146 268.745 568.891 284.443 568.891C300.146 568.891 312.891 556.146 312.891 540.443V312.891H540.443C556.146 312.891 568.891 300.146 568.891 284.443C568.891 268.745 556.146 256 540.443 256Z" fill="white"/></g></g><defs><clipPath id="clip0_10581_153"><rect width="569" height="569" fill="white"/></clipPath></defs></svg>`;
export const ADD_BUTTON_ICON_URI = `data:image/svg+xml,${encodeURIComponent(ADD_BUTTON_SVG)}`;

import { ACTION_LINK_PATTERNS, getActionLabel } from 'src/shared/constants/action-link-patterns';
import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { buildCommentFeedbackPayload } from 'src/shared/util/comment-feedback';
import { cleanVoiceTranscript } from 'src/shared/util/voice-transcript';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { DisputeResolutionPage } from 'src/pages/concierge/dispute-resolution/dispute-resolution';

interface SuggestionPill {
  id: number;
  text: string;
  displayText: string;
  icon: string;
  timeAgo: string;
  rentalOnly?: boolean;
}

@Component({
  selector: 'tidy-expandable-chat',
  templateUrl: './expandable-chat.component.html',
  styleUrls: ['./expandable-chat.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpandableChatComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private readonly SCROLL_THRESHOLD = 100;
  private readonly PENDING_INDICATOR_TIMEOUT = 60_000;
  private readonly PENDING_INDICATOR_DELAY = 1_000;
  readonly attachIconUri = ATTACH_ICON_URI;
  readonly addButtonIconUri = ADD_BUTTON_ICON_URI;
  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLDivElement>;
  @ViewChild('chatMessageInput') chatMessageInput: any;
  @ViewChild('voiceVideoElement') voiceVideoElement: ElementRef<HTMLVideoElement>;
  @ViewChild('soundBarsContainer') soundBarsContainer: ElementRef<HTMLDivElement>;
  @ViewChild('voiceOrb') voiceOrb: ElementRef<HTMLDivElement>;
  @Input() propertySummary = '';
  @Input() placeholderText = 'What can we do for you?';
  @Input() isDocked = false;
  @Input() isFullPage = false;
  @Input() set prefillMessage(value: string) {
    if (value) {
      this.setMessageAndOpen(value);
    }
  }
  @Output() messageSent = new EventEmitter<string>();
  @HostBinding('class.expanded') get expandedClass() {
    return this.isOpen();
  }
  @HostBinding('class.full-height') get fullHeightClass() {
    return this.isFullHeight();
  }
  @HostBinding('class.docked') get dockedClass() {
    return this.isDocked;
  }
  @HostBinding('class.voice-mode') get voiceModeClass() {
    return this.isVoiceMode();
  }
  @HostBinding('class.full-page') get fullPageClass() {
    return this.isFullPage;
  }

  private readonly auth = inject(Auth);
  private readonly authModalService = inject(AuthModalService);
  private readonly chatDockService = inject(ChatDockService);
  private readonly client = inject(Client);
  private readonly chatLinkRedirect = inject(ChatLinkRedirectService);
  private readonly autosuggestService = inject(ChatAutosuggestService);
  private readonly communication = inject(Communication);
  private readonly conciergeQueries = inject(ConciergeQueriesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly el = inject(ElementRef);
  private readonly injector = inject(Injector);
  private readonly modalCtrl = inject(ModalController);
  private readonly geminiLive = inject(GeminiLiveService);
  private readonly geminiToolHandler = inject(GeminiToolHandlerService);
  private readonly genome = inject(Genome);
  private readonly llmService = inject(LlmService);
  private readonly ngZone = inject(NgZone);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly sharedLinkService = inject(SharedLinkService);
  private readonly util = inject(Util);
  private readonly conciergeApi = inject(ConciergeApiService);
  private readonly demoBlock = inject(DemoBlockService);

  readonly canDock = computed(() => this.chatDockService.canDock());

  readonly chatForm = new FormGroup({
    message: new FormControl(''),
  });

  readonly boundSendMessage = this.handleSend.bind(this);
  readonly boundAddAttachment = this.addAttachment.bind(this);

  readonly isOpen = signal(false);
  readonly isFullHeight = signal(false);
  readonly isCreatingNewChat = signal(false);
  readonly isSending = signal(false);
  readonly isTyping = signal(false);
  readonly isPillAnimationPaused = signal(false);
  readonly editingSubject = signal(false);
  readonly editingSubjectValue = signal('');
  readonly isShowingDeleteConfirm = signal(false);
  readonly isPickingAttachment = signal(false);
  readonly pendingAttachments = signal<AttachmentPreview[]>([]);
  readonly localMessages = signal<ChatMessage[]>([]);
  readonly showPendingIndicator = signal(false);
  readonly pendingMetadata = signal<any>(null);
  readonly spinnerFrame = signal('⠋');
  readonly isVoiceMode = signal(false);
  readonly voiceConnectionStatus = signal<'connecting' | 'connected' | 'idle'>('idle');
  readonly isMuted = signal(false);
  readonly isAudioMuted = signal(false);
  readonly isVideoOn = signal(false);
  readonly isAiSpeaking = signal(false);
  readonly liveTranscript = signal('');
  readonly modelTranscript = signal('');
  readonly suppressAudio = signal(false);
  readonly thinkingSpinner = signal('');
  private thinkingIntervalId: ReturnType<typeof setInterval> | null = null;
  readonly isGeminiReconnecting = signal(false);
  readonly barIndices = Array.from({ length: 11 }, (_, i) => i);
  private geminiAnalyser: AnalyserNode | null = null;
  private voiceAnimationFrameId: number | null = null;
  private smoothedVolume = 0;
  readonly isScanning = signal(false);
  readonly isPasswordOverlayVisible = signal(false);
  readonly passwordError = signal('');
  readonly passwordForm = new FormGroup({
    password: new FormControl('', Validators.required),
    confirmPassword: new FormControl('', Validators.required),
  });
  readonly pendingAssets = signal<{ id: string; categoryName: string; addressName: string; iconUrl: string; args: any; corner: number }[]>([]);
  private pendingPasswordToolCallId: string | null = null;
  private nextAssetCorner = 0;
  private videoStream: MediaStream | null = null;
  private geminiEventsSubscribed = false;
  private waitingForGreeting = false;
  readonly awaitingFirstUserSpeech = signal(false);
  private geminiAudioContext: AudioContext | null = null;
  private geminiAudioProcessor: ScriptProcessorNode | null = null;
  private geminiMicStream: MediaStream | null = null;
  private geminiPlayerContext: AudioContext | null = null;
  private geminiPlayTime = 0;
  private geminiVideoIntervalId: ReturnType<typeof setInterval> | null = null;
  private geminiVideoCanvas: HTMLCanvasElement | null = null;
  private prevFrameData: Uint8ClampedArray | null = null;
  private lastAutoScanTime = 0;
  private geminiPendingAudioSources: AudioBufferSourceNode[] = [];
  readonly activeFeedbackMessageId = signal<number | null>(null);
  readonly activeFeedbackType = signal<'positive' | 'negative'>('positive');
  readonly isSubmittingFeedback = signal(false);
  private readonly TYPING_CLEAR_DELAY = 3_000;
  private readonly MAX_PENDING_RETRIES = 2;
  private pendingIndicatorTimeout: ReturnType<typeof setTimeout> | null = null;
  private pendingIndicatorRetries = 0;
  private spinnerInterval: ReturnType<typeof setInterval> | null = null;
  private typingClearTimeout: ReturnType<typeof setTimeout> | null = null;
  private hasSeenAgentProcessing = false;
  private readonly escalationPromptedSessionIds = new Set<number>();
  private isPaginating = false;
  private pendingIndicatorLastMsgId: number | null = null;
  private waitingForAgentProcessingComplete = false;

  private readonly ASCII_SPINNERS = [
    { interval: 80, frames: ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'] },
    { interval: 80, frames: ['⣾', '⣽', '⣻', '⢿', '⡿', '⣟', '⣯', '⣷'] },
    { interval: 80, frames: ['⠋', '⠙', '⠚', '⠞', '⠖', '⠦', '⠴', '⠲', '⠳', '⠓'] },
    { interval: 80, frames: ['⠄', '⠆', '⠇', '⠋', '⠙', '⠸', '⠰', '⠠', '⠰', '⠸', '⠙', '⠋', '⠇', '⠆'] },
    { interval: 80, frames: ['⠋', '⠙', '⠚', '⠒', '⠂', '⠂', '⠒', '⠲', '⠴', '⠦', '⠖', '⠒', '⠐', '⠐', '⠒', '⠓', '⠋'] },
    { interval: 80, frames: ['⠁', '⠉', '⠙', '⠚', '⠒', '⠂', '⠂', '⠒', '⠲', '⠴', '⠤', '⠄', '⠄', '⠤', '⠴', '⠲', '⠒', '⠂', '⠂', '⠒', '⠚', '⠙', '⠉', '⠁'] },
    { interval: 80, frames: ['⠈', '⠉', '⠋', '⠓', '⠒', '⠐', '⠐', '⠒', '⠖', '⠦', '⠤', '⠠', '⠠', '⠤', '⠦', '⠖', '⠒', '⠐', '⠐', '⠒', '⠓', '⠋', '⠉', '⠈'] },
    { interval: 80, frames: ['⠁', '⠁', '⠉', '⠙', '⠚', '⠒', '⠂', '⠂', '⠒', '⠲', '⠴', '⠤', '⠄', '⠄', '⠤', '⠠', '⠠', '⠤', '⠦', '⠖', '⠒', '⠐', '⠐', '⠒', '⠓', '⠋', '⠉', '⠈', '⠈'] },
  ];

  readonly isRentalClient = signal(localStorage.getItem('isRentalClient') === 'true');
  readonly isLoadingMoreMessages = computed(() => this.conciergeQueries.isLoadingMoreMessages());
  readonly hasMoreMessages = computed(() => this.conciergeQueries.hasMoreMessages());
  readonly isEscalated = computed(() => this.conciergeQueries.isEscalated());
  readonly isAgentProcessing = computed(() => this.conciergeQueries.isAgentProcessing());
  readonly isManualRefreshing = signal(false);

  readonly sessions = computed(() =>
    this.conciergeQueries.sessions().filter(s => s.title !== 'Damage Claim')
  );
  readonly isLoading = this.conciergeQueries.isLoadingSessions;
  readonly isLoadingSession = this.conciergeQueries.isLoadingMessages;
  readonly currentSessionId = this.conciergeQueries.selectedSessionId;

  readonly messages = computed(() => {
    const serverMessages = this.conciergeQueries.selectedMessages();
    const local = this.localMessages();
    if (local.length > 0) {
      const serverIds = new Set(serverMessages.map(m => m.id));
      const newLocalMessages = local.filter(m => !serverIds.has(m.id));
      if (newLocalMessages.length === 0) {return serverMessages;}

      // Exclude server messages that duplicate local attachment messages
      // (server modifies attachment URLs to a different S3 bucket that may not be immediately loadable)
      const localAttachmentIds = new Set(
        newLocalMessages.filter(m => m.attachments?.length).map(m => m.id)
      );
      const localAttachmentTimestamps = new Set(
        newLocalMessages.filter(m => m.attachments?.length).map(m => m.timestamp)
      );
      const filteredServer = localAttachmentIds.size > 0
        ? serverMessages.filter(m => {
            if (!(m.role === 'user' && m.attachments?.length)) {return true;}
            // Only filter out server messages whose timestamp closely matches a local attachment message
            if (m.timestamp && localAttachmentTimestamps.has(m.timestamp)) {return false;}
            // Check if this server message was sent within 30 seconds of a local attachment message
            const mTime = m.timestamp ? new Date(m.timestamp).getTime() : 0;
            for (const localMsg of newLocalMessages) {
              if (!localMsg.attachments?.length) {continue;}
              const localTime = localMsg.timestamp ? new Date(localMsg.timestamp).getTime() : 0;
              if (Math.abs(mTime - localTime) < 30000 && m.content === localMsg.content) {return false;}
            }
            return true;
          })
        : serverMessages;

      const combined = [...filteredServer, ...newLocalMessages];
      combined.sort((a, b) => {
        const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return ta - tb;
      });
      return combined;
    }
    return serverMessages;
  });

  readonly isDisputeResolved = computed(() => {
    const msgs = this.messages();
    return msgs.some(m => m.role === 'ai' && /resolved|resolution will be applied/i.test(m.content));
  });

  readonly currentSession = computed(() => {
    const sessionId = this.currentSessionId();
    if (!sessionId) {return undefined;}
    return this.sessions().find(s => s.id === sessionId);
  });

  readonly sessionSelectItems = computed<SelectModel[]>(() =>
    this.sessions().map(session => ({
      value: session.id.toString(),
      viewValue: session.title,
      metadata: this.formatSessionDate(session.timestamp),
    }))
  );

  readonly historySearchQuery = signal('');
  readonly filteredSessionItems = computed(() => {
    const query = this.historySearchQuery().toLowerCase().trim();
    const items = this.sessionSelectItems();
    if (!query) {return items;}
    return items.filter(item =>
      item.viewValue.toLowerCase().includes(query)
    );
  });

  readonly topRowSuggestions = signal<SuggestionPill[]>([]);
  readonly middleRowSuggestions = signal<SuggestionPill[]>([]);
  readonly bottomRowSuggestions = signal<SuggestionPill[]>([]);
  readonly autoSuggestResults = signal<ChatSuggestionResult[]>([]);
  readonly highlightedSuggestIndex = signal(-1);
  readonly thinkingPhrase = signal('');
  readonly showThinkingPhrase = signal(false);

  readonly sharedLinkCache = this.sharedLinkService.cacheSignal;
  private readonly sharedLinkUuidRegex = /\/shared-link\/([a-f0-9-]+)/g;

  readonly collapsedPreview = computed(() => {
    const lastMsg = this.messages()[this.messages().length - 1];
    if (!lastMsg?.content) {
      return '';
    }
    const cache = this.sharedLinkCache();
    const text = lastMsg.content.replace(/https?:\/\/\S+/g, (url) => {
      for (const pattern of ACTION_LINK_PATTERNS) {
        if (pattern.pattern.test(url)) {
          if (pattern.uuidPattern) {
            const uuidMatch = url.match(pattern.uuidPattern);
            const uuid = uuidMatch?.[1];
            if (uuid && cache.has(uuid)) {
              const data = cache.get(uuid);
              return getActionLabel(data.shared_link_data.action_type);
            }
          }
          return pattern.label;
        }
      }
      return url;
    });
    const maxLen = 60;
    return text.length > maxLen ? `${text.substring(0, maxLen)  }...` : text;
  });

  private clickListener: () => void;
  private preExistingServerIds = new Set<number>();
  private shouldScrollToBottom = false;
  private scrollListenerSetup = false;
  private messageLinkListenerSetup = false;
  private thinkingPhraseTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public windowService: WindowService
  ) {
    this.clickListener = this.onDocumentClick.bind(this);

    effect(() => {
      const session = this.currentSession();
      if (session && !this.isCreatingNewChat()) {
        localStorage.setItem('selectedChatSessionId', session.id.toString());
      }
    });

    // Scroll to bottom when messages load for a new session
    effect(() => {
      const messages = this.messages();
      const isLoading = this.isLoadingSession();
      const isOpen = this.isOpen();
      const isTyping = this.isTyping();
      const showPending = this.showPendingIndicator();

      // Only scroll when messages finish loading (not during infinite scroll).
      // Use setTimeout because effects run AFTER ngAfterViewChecked in the same
      // CD cycle, so the shouldScrollToBottom flag would be picked up too late.
      if (isOpen && messages.length > 0 && !isLoading && !this.conciergeQueries.isLoadingMoreMessages() && !this.isPaginating) {
        setTimeout(() => this.scrollToBottom());
      }
    });

    // Clear localMessages individually when each appears in server messages (by matching content and role)
    effect(() => {
      const serverMessages = this.conciergeQueries.selectedMessages();
      const local = this.localMessages();
      if (serverMessages.length > 0 && local.length > 0) {
        const newServerMessages = serverMessages.filter(m => !this.preExistingServerIds.has(m.id));
        const remaining = local.filter(localMsg => {
          if (localMsg.attachments?.length > 0) {return true;}
          return !newServerMessages.some(serverMsg => serverMsg.content === localMsg.content && serverMsg.role === localMsg.role);
        });
        if (remaining.length < local.length) {
          this.localMessages.set(remaining);
        }
      }
    });

    // Clear pending indicator only when a NEW ai message arrives AND agent is done processing
    // Ignore AiFrontendAgent messages (Gemini acknowledgments) — only clear for real agent responses
    effect(() => {
      const serverMessages = this.conciergeQueries.selectedMessages();
      if (serverMessages.length > 0) {
        const lastMessage = serverMessages[serverMessages.length - 1];
        if (lastMessage.role === 'ai' && lastMessage.userType !== 'AiFrontendAgent' && lastMessage.id !== this.pendingIndicatorLastMsgId) {
          if (this.waitingForAgentProcessingComplete) {
            this.pendingIndicatorLastMsgId = lastMessage.id;
          } else {
            this.clearPendingIndicator();
          }
        }
      }
    });

    // Enable/disable message polling based on chat state
    effect(() => {
      const isOpen = this.isOpen();
      const hasSession = this.currentSessionId() !== null;
      const isCreatingNew = this.isCreatingNewChat();
      this.conciergeQueries.setPollingEnabled(isOpen && hasSession && !isCreatingNew);
    });

    // Clear typing with a delay when agent stops via SSE, so the new message
    // has time to arrive and replace the indicator without a visible flicker.
    effect(() => {
      const sseAgent = this.conciergeQueries.sseAgentStatus();
      if (sseAgent && sseAgent.status !== 'running') {
        this.scheduleTypingClear();
      }
    });

    // Clear pending indicator when agent stops processing (true → false transition)
    effect(() => {
      const processing = this.isAgentProcessing();
      if (processing) {
        this.hasSeenAgentProcessing = true;
      } else if (this.hasSeenAgentProcessing || this.waitingForAgentProcessingComplete) {
        this.hasSeenAgentProcessing = false;
        this.waitingForAgentProcessingComplete = false;
        this.clearPendingIndicator();
      }
    });

    // Start/stop ASCII spinner animation based on loading states
    effect(() => {
      const shouldAnimate = this.showPendingIndicator() || this.isTyping() || this.isLoading();
      if (shouldAnimate) {
        this.startSpinner();
      } else {
        this.stopSpinner();
      }
    });

    // Fetch shared link metadata for inline rendering (e.g., payment forms)
    effect(() => {
      const msgs = this.messages();
      const uuidRegex = /\/shared-link\/([a-f0-9-]+)/g;
      for (const msg of msgs) {
        if (!msg.content) {continue;}
        let match: RegExpExecArray | null;
        while ((match = uuidRegex.exec(msg.content)) !== null) {
          this.sharedLinkService.fetchAndCache(match[1]);
        }
      }
    });

    // Voice mode: when a new AI concierge reply arrives for an in-flight lookup,
    // inject it into Gemini so the voice response is spoken.
    effect(() => {
      const lookup = this.pendingLookup();
      if (!lookup || lookup.resolved || lookup.conciergeItemId <= 0) {return;}
      const msgs = this.conciergeQueries.selectedMessages();
      const newAi = msgs.slice(lookup.firstNewAiMsgIndex).filter(m => m.role === 'ai' && m.content);
      if (newAi.length === 0) {return;}
      if (this.isEscalated()) {
        this.suppressAudio.set(true);
        this.finalizeLookup(lookup, '[ESCALATED]');
      } else {
        const combined = newAi.map(m => m.content).join('\n');
        const stripped = this.stripUrlsForVoice(combined) || 'No new information.';
        this.finalizeLookup(lookup, `[CONCIERGE_RESPONSE: ${stripped}]`);
      }
    });

    // A placeholder account only exists on this device's token, so a human's
    // reply to an escalated thread is unreachable once the session is gone.
    // Ask them to create an account the moment a thread escalates.
    effect(() => {
      const sessionId = this.currentSessionId();
      if (!this.isEscalated() || sessionId === null) {return;}
      this.promptCreateAccountForEscalation(sessionId);
    });

  }

  // Mirrors the backend's Customer#placeholder_account?. Deliberately checks the
  // email rather than the isGuestAccount flag, which is also set for the shared
  // demo account (that one has its own gating via DemoBlockService).
  private isPlaceholderAccount(): boolean {
    return (localStorage.getItem('email') || '').includes('placeholder.tidy');
  }

  private async promptCreateAccountForEscalation(sessionId: number): Promise<void> {
    if (this.escalationPromptedSessionIds.has(sessionId) || !this.isPlaceholderAccount()) {
      return;
    }
    this.escalationPromptedSessionIds.add(sessionId);
    await this.authModalService.openAuthModal('auth', 'signup', 'Create an Account to Get Your Answer');
  }

  ngOnInit(): void {
    if (!this.isFullPage) {
      document.addEventListener('click', this.clickListener, true);
    }
    this.initializeSuggestions();
    this.rightSidePanelService.sendMessageAction$.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(action => action?.type === 'prefill')
    ).subscribe(action => {
      this.pendingMetadata.set(action.metadata || null);
      this.setMessageAndOpen(action.message);
    });
    this.chatForm.get('message')!.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.updateAutoSuggest(value || '');
    });
    if (!this.auth.isAuthenticated()) {
      return;
    }
    this.restoreSessionFromStorage();
    this.checkAutoOpen();
    if (this.isDocked || this.isFullPage) {
      this.isOpen.set(true);
      this.shouldScrollToBottom = true;
    }
    // Before the refetch: a prefill starts a new chat, and a refetch of the
    // previous chat landing after that would put its messages back on screen.
    this.consumePendingPrefill();
    if (this.isFullPage && this.currentSessionId() !== null) {
      this.isManualRefreshing.set(true);
      this.conciergeQueries.refetchMessages().then(() => {
        this.isManualRefreshing.set(false);
      });
    }
  }

  // Pages without a mounted chat (e.g. Billing History's "Get Help") stash a
  // prefill in localStorage and navigate here; sendMessageAction$ is a plain
  // Subject, so an emit before this component mounts would be lost.
  private consumePendingPrefill(): void {
    const message = localStorage.getItem('chatPrefillMessage');
    if (!message) {
      return;
    }
    localStorage.removeItem('chatPrefillMessage');
    const metadataRaw = localStorage.getItem('chatPrefillMetadata');
    localStorage.removeItem('chatPrefillMetadata');
    let metadata = null;
    if (metadataRaw) {
      try {
        metadata = JSON.parse(metadataRaw);
      } catch {
        // Malformed stored metadata — ignore and leave it null.
        metadata = null;
      }
    }
    this.pendingMetadata.set(metadata);
    this.setMessageAndOpen(message);
  }

  dockToSiderail(): void {
    this.chatDockService.dock();
  }

  undockFromSiderail(): void {
    this.chatDockService.undock();
  }

  // Open immediately to avoid a visible collapsed→expanded flash; refetch in background
  private checkAutoOpen(): void {
    const autoOpen = localStorage.getItem('autoOpenExpandableChat');
    if (autoOpen === 'true') {
      localStorage.removeItem('autoOpenExpandableChat');
      this.openChat();
      this.conciergeQueries.refetchSessions();
    }
  }

  private listenForPanelClose(): void {
    this.rightSidePanelService.afterPanelClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.checkAutoOpen();
    });
  }

  ngAfterViewInit(): void {
    this.setupScrollListener();
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.clickListener, true);
    const container = this.scrollContainer?.nativeElement;
    if (container) {
      container.removeEventListener('click', this.onMessagesContainerClick);
    }
    this.conciergeQueries.setPollingEnabled(false);
    if (this.pendingIndicatorTimeout) {
      clearTimeout(this.pendingIndicatorTimeout);
    }
    this.stopSpinner();
    this.cleanupVoiceMode();
    this.stopVideoStream();
    if (this.typingClearTimeout) {
      clearTimeout(this.typingClearTimeout);
    }
    if (this.thinkingPhraseTimeout) {
      clearTimeout(this.thinkingPhraseTimeout);
    }
  }

  private initializeSuggestions(): void {
    const allSuggestions: SuggestionPill[] = [
      { id: 1, text: 'Schedule a cleaning', displayText: 'Scheduled a cleaning', icon: 'cleaning_services', timeAgo: this.randomTimeAgo() },
      { id: 2, text: 'Request a repair', displayText: 'Requested a repair', icon: 'build', timeAgo: this.randomTimeAgo() },
      { id: 3, text: 'Get a property overview', displayText: 'Reviewed their property', icon: 'dashboard', timeAgo: this.randomTimeAgo() },
      { id: 4, text: 'Check my schedule', displayText: 'Checked their schedule', icon: 'calendar_today', timeAgo: this.randomTimeAgo() },
      { id: 5, text: 'Review my finances', displayText: 'Reviewed their finances', icon: 'payments', timeAgo: this.randomTimeAgo() },
      { id: 6, text: 'Report an issue', displayText: 'Reported an issue', icon: 'flag', timeAgo: this.randomTimeAgo() },
      { id: 7, text: 'Find a pro', displayText: 'Found a new pro', icon: 'person_search', timeAgo: this.randomTimeAgo() },
      { id: 8, text: 'Schedule a property inspection', displayText: 'Scheduled an inspection', icon: 'search', timeAgo: this.randomTimeAgo() },
      { id: 9, text: 'Restock supplies', displayText: 'Restocked supplies', icon: 'shopping_cart', timeAgo: this.randomTimeAgo(), rentalOnly: true },
      { id: 10, text: 'Schedule a plumber', displayText: 'Booked a plumber', icon: 'plumbing', timeAgo: this.randomTimeAgo() },
      { id: 11, text: 'Schedule a handyman', displayText: 'Booked a handyman', icon: 'handyman', timeAgo: this.randomTimeAgo() },
      { id: 12, text: 'Schedule HVAC service', displayText: 'Scheduled HVAC service', icon: 'ac_unit', timeAgo: this.randomTimeAgo() },
      { id: 13, text: 'Schedule appliance repair', displayText: 'Scheduled appliance repair', icon: 'bolt', timeAgo: this.randomTimeAgo() },
      { id: 14, text: 'Schedule lawn care', displayText: 'Booked lawn care', icon: 'grass', timeAgo: this.randomTimeAgo() },
      { id: 15, text: 'Schedule a junk removal', displayText: 'Scheduled junk removal', icon: 'local_shipping', timeAgo: this.randomTimeAgo() },
      { id: 16, text: 'Update property access info', displayText: 'Updated access info', icon: 'vpn_key', timeAgo: this.randomTimeAgo() },
      { id: 17, text: 'Track an appliance or asset', displayText: 'Tracked a new appliance', icon: 'inventory_2', timeAgo: this.randomTimeAgo() },
      { id: 18, text: 'Check on a maintenance request', displayText: 'Checked on a repair', icon: 'engineering', timeAgo: this.randomTimeAgo() },
      { id: 19, text: 'Send a message to my pro', displayText: 'Messaged their pro', icon: 'send', timeAgo: this.randomTimeAgo() },
      { id: 20, text: 'Submit an expense receipt', displayText: 'Submitted a receipt', icon: 'receipt', timeAgo: this.randomTimeAgo() },
      { id: 21, text: 'Check my property taxes', displayText: 'Reviewed property taxes', icon: 'account_balance', timeAgo: this.randomTimeAgo() },
      { id: 22, text: 'Update a cleaning checklist', displayText: 'Updated a checklist', icon: 'checklist', timeAgo: this.randomTimeAgo() },
      { id: 23, text: 'Schedule a call with TIDY', displayText: 'Scheduled a call', icon: 'call', timeAgo: this.randomTimeAgo() },
      { id: 24, text: 'Review a reservation', displayText: 'Reviewed a reservation', icon: 'hotel', timeAgo: this.randomTimeAgo(), rentalOnly: true },
      { id: 25, text: 'Review guest messages', displayText: 'Reviewed guest messages', icon: 'forum', timeAgo: this.randomTimeAgo(), rentalOnly: true },
      { id: 26, text: 'Check compliance status', displayText: 'Checked compliance status', icon: 'verified_user', timeAgo: this.randomTimeAgo(), rentalOnly: true },
      { id: 27, text: 'Update my guidebook', displayText: 'Updated their guidebook', icon: 'menu_book', timeAgo: this.randomTimeAgo(), rentalOnly: true },
      { id: 28, text: 'Check insurance coverage', displayText: 'Reviewed insurance coverage', icon: 'shield', timeAgo: this.randomTimeAgo(), rentalOnly: true },
    ];
    const filteredSuggestions = allSuggestions.filter(
      s => !s.rentalOnly || this.isRentalClient()
    );
    const thirdPoint = Math.ceil(filteredSuggestions.length / 3);
    const twoThirdsPoint = Math.ceil(filteredSuggestions.length * 2 / 3);
    this.topRowSuggestions.set(filteredSuggestions.slice(0, thirdPoint));
    this.middleRowSuggestions.set(filteredSuggestions.slice(thirdPoint, twoThirdsPoint));
    this.bottomRowSuggestions.set(filteredSuggestions.slice(twoThirdsPoint));
  }

  private randomTimeAgo(): string {
    const minutes = [2, 3, 5, 7, 8, 12, 15, 18, 22, 28, 33, 45];
    const m = minutes[Math.floor(Math.random() * minutes.length)];
    return `${m}m ago`;
  }

  private setupScrollListener(): void {
    if (this.scrollListenerSetup) {return;}

    const container = this.scrollContainer?.nativeElement;
    if (!container) {return;}

    this.scrollListenerSetup = true;

    fromEvent(container, 'scroll').pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(100),
      filter(() => !this.conciergeQueries.isLoadingMoreMessages() && this.isOpen())
    ).subscribe(() => {
      const isNearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 50;
      if (container.scrollTop < this.SCROLL_THRESHOLD && this.hasMoreMessages() && !isNearBottom) {
        this.loadMoreMessages();
      }
    });
  }

  private readonly onMessagesContainerClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a') as HTMLAnchorElement;
    if (!anchor) {
      return;
    }
    const messageText = anchor.closest('.message-text');
    if (!messageText) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const url = anchor.href;
    if (!url) {
      return;
    }
    this.chatLinkRedirect.handleLink(url).then(result => {
      if (result.handled && !this.isFullPage && !this.isDocked) {
        this.closeChat();
      }
    });
  };

  private setupMessageLinkListener(): void {
    const container = this.scrollContainer?.nativeElement;
    if (!container) {
      return;
    }
    container.addEventListener('click', this.onMessagesContainerClick);
  }

  private async loadMoreMessages(): Promise<void> {
    this.isPaginating = true;
    const scrollContainer = this.scrollContainer?.nativeElement;
    const previousScrollHeight = scrollContainer?.scrollHeight || 0;

    await this.conciergeQueries.loadMoreMessages();

    // Maintain scroll position after prepending messages
    // Use setTimeout to ensure DOM has updated before adjusting scroll
    setTimeout(() => {
      if (scrollContainer) {
        const newScrollHeight = scrollContainer.scrollHeight;
        scrollContainer.scrollTop = newScrollHeight - previousScrollHeight;
      }
      this.isPaginating = false;
    }, 0);
  }

  private onDocumentClick(event: Event): void {
    if (this.isDocked || this.isFullPage) {return;}
    const target = event.target as HTMLElement;
    if (this.isOpen() && !this.el.nativeElement.contains(target)) {
      if (this.isShowingDeleteConfirm() || this.isPickingAttachment()) {
        return;
      }
      if (target.closest('.cdk-overlay-container')) {
        return;
      }
      if (target.hasAttribute('data-download-trigger')) {
        return;
      }
      this.closeChat();
    }
  }

  private restoreSessionFromStorage(): void {
    effect(
      () => {
        const sessions = this.sessions();
        const isLoading = this.isLoading(); // Track as dependency

        if (sessions.length > 0 && this.currentSessionId() === null && !this.isCreatingNewChat()) {
          // Reset isCreatingNewChat since we have sessions to select (only if user didn't explicitly request new chat)

          const savedSessionId = localStorage.getItem('selectedChatSessionId');
          const savedSession = savedSessionId
            ? sessions.find(s => s.id === parseInt(savedSessionId, 10))
            : null;
          if (savedSession) {
            this.conciergeQueries.selectSession(savedSession.id);
          } else {
            this.conciergeQueries.selectSession(sessions[0].id);
          }
        } else if (sessions.length === 0 && !isLoading) {
          this.isCreatingNewChat.set(true);
          this.focusInput();
        } else if (sessions.length > 0 && this.isFullPage && !Capacitor.isNativePlatform()) {
          this.focusInput();
        }
      },
      { injector: this.injector }
    );
  }


  shouldShowTimeIndicator(index: number): boolean {
    const msgs = this.messages();
    if (index === 0) {
      return true;
    }
    const currentMessage = msgs[index];
    const previousMessage = msgs[index - 1];
    if (!currentMessage.timestamp || !previousMessage.timestamp) {
      return false;
    }
    const currentDate = new Date(currentMessage.timestamp);
    const previousDate = new Date(previousMessage.timestamp);
    const diffInMinutes = (currentDate.getTime() - previousDate.getTime()) / (1000 * 60);
    return diffInMinutes > 30;
  }

  formatMessageTime(timestamp: string): string {
    if (!timestamp) {
      return '';
    }
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (isToday) {
      return timeStr;
    } else if (isYesterday) {
      return `Yesterday ${timeStr}`;
    } else {
      return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${timeStr}`;
    }
  }

  isLastInGroup(index: number): boolean {
    const msgs = this.messages();
    if (index === msgs.length - 1) {
      return true;
    }
    const currentMessage = msgs[index];
    const nextMessage = msgs[index + 1];
    return currentMessage.role !== nextMessage.role;
  }

  private focusInput(): void {
    setTimeout(() => this.chatMessageInput?.focusInput(), 100);
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom && this.scrollContainer && this.isOpen()) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
    if (!this.messageLinkListenerSetup && this.scrollContainer?.nativeElement && this.isOpen()) {
      this.setupMessageLinkListener();
      this.messageLinkListenerSetup = true;
    }
  }

  scrollToBottom(): void {
    const container = this.scrollContainer?.nativeElement;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }

  openChat(): void {
    this.isOpen.set(true);
    this.shouldScrollToBottom = true;
    this.focusInput();
    setTimeout(() => this.setupScrollListener(), 0);

    const sessions = this.sessions();

    if (sessions.length > 0 && this.currentSessionId() === null && !this.isCreatingNewChat()) {
      // Have sessions and not creating new - select first (or saved session)
      const savedSessionId = localStorage.getItem('selectedChatSessionId');
      const savedSession = savedSessionId
        ? sessions.find(s => s.id === parseInt(savedSessionId, 10))
        : null;
      this.conciergeQueries.selectSession(savedSession?.id ?? sessions[0].id);
    } else if (sessions.length === 0 && !this.isLoading()) {
      // No sessions and not loading - show New Chat
      this.isCreatingNewChat.set(true);
    }

    // Trigger refetch for authenticated users if needed
    if (this.auth.isAuthenticated() && sessions.length === 0 && !this.isLoading()) {
      this.conciergeQueries.refetchSessions();
    }

    // Refetch messages to get fresh attachment URLs from the server
    if (this.currentSessionId() !== null) {
      this.conciergeQueries.refetchMessages();
    }
  }

  setMessageAndOpen(message: string, fullHeight = false): void {
    this.isFullHeight.set(fullHeight);
    this.chatForm.patchValue({ message });
    // New chat first, so openChat() doesn't refetch the previous one's messages.
    this.createNewSession();
    this.openChat();
    this.focusInput();
  }

  closeChat(event?: Event): void {
    event?.stopPropagation();
    this.isOpen.set(false);
    this.isFullHeight.set(false);
    this.localMessages.set([]);
    this.messageLinkListenerSetup = false;
  }

  preserveInputFocus(event: MouseEvent): void {
    if (!this.isOpen()) {
      return;
    }
    if (Capacitor.isNativePlatform()) {
      return;
    }
    const target = event.target as HTMLElement;
    const tag = target.tagName.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') {
      return;
    }
    event.preventDefault();
  }

  onContainerClick(): void {
    if (!this.isOpen()) {
      this.openChat();
    }
  }

  async handleSpeakClick(): Promise<void> {
    // The demo account's chat is read-only — starting a voice session writes to it.
    if (this.demoBlock.block()) {return;}
    if (this.isVoiceMode()) {
      this.exitVoiceMode();
      return;
    }
    // The backend agent doesn't answer on a chat our team has, so voice gets a chat of its own.
    if (this.conciergeQueries.isHandedOff()) {
      this.createNewSession(false);
    }
    this.chatMessageInput?.blurInput();
    this.isVoiceMode.set(true);
    this.shouldScrollToBottom = true;
    this.voiceConnectionStatus.set('connecting');
    await this.connectToGemini();
    if (!this.isVoiceMode()) {
      return;
    }
    this.voiceConnectionStatus.set('connected');
    const ticketId = this.currentSession()?.supportTicketId || null;
    this.geminiToolHandler.setVoiceSessionTicketId(ticketId);
  }

  exitVoiceMode(): void {
    this.cleanupVoiceMode();
    this.stopVideoStream();
    this.isVoiceMode.set(false);
    this.voiceConnectionStatus.set('idle');
    this.isMuted.set(false);
    this.isVideoOn.set(false);
    this.isAiSpeaking.set(false);
    this.conciergeQueries.refetchMessages();
  }

  toggleMute(): void {
    const muted = !this.isMuted();
    this.isMuted.set(muted);
    this.geminiMicStream?.getAudioTracks().forEach(track => { track.enabled = !muted; });
  }

  toggleAudioMute(): void {
    this.isAudioMuted.set(!this.isAudioMuted());
  }

  async handleVideoClick(): Promise<void> {
    if (this.isVideoOn()) {
      this.stopGeminiVideoStreaming();
      this.stopVideoStream();
      this.isVideoOn.set(false);
      return;
    }
    try {
      this.videoStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' }
      });
      this.isVideoOn.set(true);
      this.cdr.detectChanges();
      const video = this.voiceVideoElement?.nativeElement;
      if (video) {
        video.srcObject = this.videoStream;
        if (this.geminiLive.isConnected()) {
          this.startGeminiVideoStreaming();
        }
      }
    } catch {
      this.util.showError('Unable to access camera');
    }
  }

  private stopVideoStream(): void {
    this.stopGeminiVideoStreaming();
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
      this.videoStream = null;
    }
  }

  async handleSend(): Promise<void> {
    if (this.demoBlock.block()) {return;}
    const messageValue = this.chatForm.get('message')?.value?.trim() || '';
    if (!messageValue && this.pendingAttachments().length === 0) {
      return;
    }
    if (this.isSending()) {
      return;
    }
    if (!this.isOpen()) {
      this.openChat();
    }
    const attachmentUrls = this.pendingAttachments().map(a => a.url);
    const userMessage: ChatMessage = {
      id: Date.now(),
      role: 'user',
      content: messageValue || (this.pendingAttachments().length > 0 ? 'Attachment' : ''),
      timestamp: new Date().toISOString(),
      attachments: attachmentUrls,
    };
    this.preExistingServerIds = new Set(this.conciergeQueries.selectedMessages().map(m => m.id));
    this.localMessages.update(msgs => [...msgs, userMessage]);
    this.messageSent.emit(messageValue);
    const attachmentsToSend = [...attachmentUrls];
    this.chatForm.reset();
    this.pendingAttachments.set([]);
    this.shouldScrollToBottom = true;
    this.isSending.set(true);
    this.thinkingPhrase.set(
      this.autosuggestService.getThinkingPhrase(messageValue, this.isRentalClient())
    );
    this.showThinkingPhrase.set(false);
    try {
      const serverMsgs = this.conciergeQueries.selectedMessages();
      this.pendingIndicatorLastMsgId = serverMsgs.length > 0 ? serverMsgs[serverMsgs.length - 1].id : null;
      this.showPendingIndicator.set(true);
      this.shouldScrollToBottom = true;
      if (this.pendingIndicatorTimeout) {
        clearTimeout(this.pendingIndicatorTimeout);
      }
      this.pendingIndicatorRetries = 0;
      this.schedulePendingIndicatorCheck();
      // Show contextual thinking dialog after 3 seconds
      if (this.thinkingPhraseTimeout) {
        clearTimeout(this.thinkingPhraseTimeout);
      }
      this.thinkingPhraseTimeout = setTimeout(() => {
        this.showThinkingPhrase.set(true);
        this.shouldScrollToBottom = true;
      }, 3000);

      if (this.isCreatingNewChat()) {
        const metadata = this.pendingMetadata();
        const subject = messageValue
          ? await this.summarizeSubject(messageValue)
          : 'Support Request';
        const item: any = {
          data: {
            subject,
            body: messageValue || 'Attachment',
            attachments: attachmentsToSend,
            emails: [],
          },
          type: metadata ? 'support.billing' : 'support.other',
        };
        if (metadata) {
          item.data.metadata = metadata;
        }
        const response = await this.conciergeQueries.createItemMutation.mutateAsync(item);
        this.pendingMetadata.set(null);
        await this.conciergeQueries.refetchSessions();
        this.conciergeQueries.selectSession(response.id);
        this.isCreatingNewChat.set(false);
        // localMessages will be cleared when server messages load
      } else {
        const session = this.currentSession();
        if (session?.supportTicketId) {
          const data = {
            support_ticket_id: session.supportTicketId,
            content: messageValue || 'Attachment',
            attachments: attachmentsToSend,
          };
          await this.conciergeQueries.addCommentMutation.mutateAsync(data);
        }
      }
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to send message');
      this.localMessages.update(msgs => msgs.filter(m => m.id !== userMessage.id));
      this.clearPendingIndicator();
    } finally {
      this.isSending.set(false);
    }
  }

  async addAttachment(): Promise<void> {
    this.isPickingAttachment.set(true);
    try {
      const attachment = await this.communication.addAttachment();
      if (!attachment?.url) {
        this.util.showError('Unable to attach file. Please try again.');
        return;
      }
      this.pendingAttachments.update(attachments => [...attachments, attachment]);
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to add attachment');
    } finally {
      this.isPickingAttachment.set(false);
    }
  }

  removeAttachment(index: number): void {
    this.pendingAttachments.update(attachments => {
      const newAttachments = [...attachments];
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  }

  downloadFile = async (url: string): Promise<void> => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.setAttribute('data-download-trigger', 'true');
      const pathname = new URL(url).pathname;
      a.download = decodeURIComponent(pathname.split('/').pop() || 'download');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    }
  }

  async selectSession(session: ChatSession): Promise<void> {
    this.conciergeQueries.selectSession(session.id);
    this.isCreatingNewChat.set(false);
    this.localMessages.set([]);
    this.clearTypingState();
    this.shouldScrollToBottom = true;
    this.focusInput();
  }

  // User-facing "New Chat" button. Distinct from createNewSession() (which is also called
  // internally after deleting the last session) so the demo guard only fires on a real click.
  newChat(): void {
    if (this.demoBlock.block()) {return;}
    this.createNewSession();
  }

  createNewSession(focus = true): void {
    this.isCreatingNewChat.set(true);
    this.conciergeQueries.selectSession(null);
    localStorage.removeItem('selectedChatSessionId');
    this.localMessages.set([]);
    this.clearTypingState();
    if (focus) {
      this.focusInput();
    }
  }

  onHistorySessionSelect(sessionValue: string): void {
    this.onSessionSelected(sessionValue);
    this.historySearchQuery.set('');
  }

  onHistoryNewChat(): void {
    if (this.demoBlock.block()) {return;}
    this.createNewSession();
    this.historySearchQuery.set('');
  }

  onInlinePaymentSuccess(): void {
    const msgs = this.conciergeQueries.selectedMessages();
    this.pendingIndicatorLastMsgId = msgs.length > 0 ? msgs[msgs.length - 1].id : null;
    this.showPendingIndicator.set(true);
    this.waitingForAgentProcessingComplete = true;
  }

  private clearPendingIndicator(): void {
    this.showPendingIndicator.set(false);
    this.thinkingPhrase.set('');
    this.showThinkingPhrase.set(false);
    this.pendingIndicatorRetries = 0;
    if (this.pendingIndicatorTimeout) {
      clearTimeout(this.pendingIndicatorTimeout);
      this.pendingIndicatorTimeout = null;
    }
    if (this.thinkingPhraseTimeout) {
      clearTimeout(this.thinkingPhraseTimeout);
      this.thinkingPhraseTimeout = null;
    }
  }

  private schedulePendingIndicatorCheck(): void {
    this.pendingIndicatorTimeout = setTimeout(() => {
      this.pendingIndicatorRetries++;
      if (this.pendingIndicatorRetries > this.MAX_PENDING_RETRIES) {
        this.showPendingIndicator.set(false);
        this.thinkingPhrase.set('');
        this.showThinkingPhrase.set(false);
        this.conciergeQueries.invalidateSelectedItem();
        return;
      }
      this.conciergeQueries.invalidateSelectedItem();
      this.schedulePendingIndicatorCheck();
    }, this.PENDING_INDICATOR_TIMEOUT);
  }

  // Delays hiding the typing indicator so the new message can arrive and
  // replace it in the same render cycle (avoids flicker). If no message
  // arrives within TYPING_CLEAR_DELAY, clears the indicator as a fallback.
  private scheduleTypingClear(): void {
    if (this.typingClearTimeout) { return; }
    this.typingClearTimeout = setTimeout(() => {
      this.typingClearTimeout = null;
      this.clearTypingState();
    }, this.TYPING_CLEAR_DELAY);
  }

  private clearTypingState(): void {
    if (this.typingClearTimeout) {
      clearTimeout(this.typingClearTimeout);
      this.typingClearTimeout = null;
    }
    this.clearPendingIndicator();
    this.isTyping.set(false);
    this.hasSeenAgentProcessing = false;
  }

  private startSpinner(): void {
    if (this.spinnerInterval) { return; }
    const spinner = this.ASCII_SPINNERS[Math.floor(Math.random() * this.ASCII_SPINNERS.length)];
    let frameIndex = 0;
    this.spinnerFrame.set(spinner.frames[0]);
    this.spinnerInterval = setInterval(() => {
      frameIndex = (frameIndex + 1) % spinner.frames.length;
      this.spinnerFrame.set(spinner.frames[frameIndex]);
    }, spinner.interval);
  }

  private stopSpinner(): void {
    if (this.spinnerInterval) {
      clearInterval(this.spinnerInterval);
      this.spinnerInterval = null;
    }
  }

  formatSessionDate(date: Date): string {
    const now = new Date();
    const sessionDate = new Date(date);
    const isToday = sessionDate.toDateString() === now.toDateString();
    if (isToday) {
      return sessionDate
        .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
        .toLowerCase();
    }
    const sixDaysAgo = new Date(now);
    sixDaysAgo.setDate(sixDaysAgo.getDate() - 6);
    sixDaysAgo.setHours(0, 0, 0, 0);
    if (sessionDate >= sixDaysAgo) {
      return sessionDate.toLocaleDateString([], { weekday: 'long' });
    }
    if (sessionDate.getFullYear() === now.getFullYear()) {
      return sessionDate.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    return sessionDate.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  trackByMessageId(_index: number, message: ChatMessage): number {
    return message.id;
  }

  getMessageFeedback(messageId: number): 'positive' | 'negative' | undefined {
    return this.conciergeQueries.messageFeedback()[messageId];
  }

  onThumbClick(message: ChatMessage, type: 'positive' | 'negative'): void {
    if (this.getMessageFeedback(message.id)) {
      return;
    }
    this.activeFeedbackMessageId.set(message.id);
    this.activeFeedbackType.set(type);
    this.shouldScrollToBottom = true;
    setTimeout(() => {
      const textarea = this.el.nativeElement.querySelector('.feedback-textarea') as HTMLTextAreaElement;
      textarea?.focus();
    }, 50);
  }

  cancelFeedback(): void {
    this.activeFeedbackMessageId.set(null);
  }

  async submitFeedback(message: ChatMessage): Promise<void> {
    const conciergeItemId = this.conciergeQueries.selectedSessionId();
    if (!conciergeItemId) {
      return;
    }
    const textarea = this.el.nativeElement.querySelector('.feedback-textarea') as HTMLTextAreaElement;
    const feedbackText = textarea?.value?.trim();
    this.isSubmittingFeedback.set(true);
    try {
      const data = buildCommentFeedbackPayload({
        commentId: message.id,
        conciergeItemId,
        type: this.activeFeedbackType(),
        text: feedbackText,
      });
      await this.conciergeQueries.submitFeedbackMutation.mutateAsync(data);
      const type = this.activeFeedbackType();
      this.conciergeQueries.setMessageFeedback(message.id, type);
      this.activeFeedbackMessageId.set(null);
      this.util.showSuccess('Feedback submitted');
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to submit feedback');
    } finally {
      this.isSubmittingFeedback.set(false);
    }
  }

  openScheduleCallModal(): void {
    this.util.openUrl('https://www.tidy.com/demo');
  }

  onSuggestionClick(suggestion: SuggestionPill): void {
    this.chatForm.get('message')?.setValue(suggestion.text);
    this.focusInput();
  }

  private updateAutoSuggest(value: string): void {
    if (!value || value.trim().length < 2) {
      this.autoSuggestResults.set([]);
      this.highlightedSuggestIndex.set(-1);
      return;
    }
    const results = this.autosuggestService.search(value, 6, this.isRentalClient());
    this.autoSuggestResults.set(results);
    this.highlightedSuggestIndex.set(-1);
  }

  onAutoSuggestSelect(suggestion: ChatSuggestionResult): void {
    this.chatForm.get('message')?.setValue(suggestion.text);
    this.autoSuggestResults.set([]);
    this.highlightedSuggestIndex.set(-1);
    this.focusInput();
  }

  onFormKeyDown(event: KeyboardEvent): void {
    const results = this.autoSuggestResults();
    if (results.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightedSuggestIndex.update(i => Math.min(i + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightedSuggestIndex.update(i => Math.max(i - 1, -1));
    } else if (event.key === 'Escape') {
      this.autoSuggestResults.set([]);
      this.highlightedSuggestIndex.set(-1);
    } else if (event.key === 'Tab' && this.highlightedSuggestIndex() >= 0) {
      event.preventDefault();
      this.onAutoSuggestSelect(results[this.highlightedSuggestIndex()]);
    }
  }

  pausePillAnimation(): void {
    this.isPillAnimationPaused.set(true);
  }

  resumePillAnimation(): void {
    this.isPillAnimationPaused.set(false);
  }

  startEditingSubject(): void {
    const session = this.currentSession();
    if (!session || this.isCreatingNewChat()) {
      return;
    }
    this.editingSubject.set(true);
    this.editingSubjectValue.set(session.title);
  }

  async saveSubject(): Promise<void> {
    if (this.isShowingDeleteConfirm()) {
      return;
    }
    const newSubject = this.editingSubjectValue().trim();
    const session = this.currentSession();
    if (!newSubject || !session) {
      this.cancelEditingSubject();
      return;
    }
    if (newSubject === session.title) {
      this.cancelEditingSubject();
      return;
    }
    try {
      await this.conciergeQueries.updateSubjectMutation.mutateAsync({
        itemId: session.id,
        subject: newSubject,
      });
      this.editingSubject.set(false);
      this.util.showSuccess('Subject updated');
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to update subject');
    }
  }

  cancelEditingSubject(): void {
    this.editingSubject.set(false);
    this.editingSubjectValue.set('');
  }

  async closeCurrentSupportRequest(event: Event): Promise<void> {
    event.stopPropagation();
    const session = this.currentSession();
    if (!session) {
      this.cancelEditingSubject();
      return;
    }
    this.isShowingDeleteConfirm.set(true);
    const params: ConfirmPageParamsModel = {
      title: 'Delete Chat?',
      body: 'Are you sure you want to delete this chat?',
      backText: 'Go Back',
      confirmText: 'Delete',
      confirmAction: this.confirmCloseCurrentSupportRequest.bind(this),
      customBackAction: this.cancelDeleteConfirm.bind(this),
      confirmButtonClass: 'primary',
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal',
    });
    await confirmationModal.present();
    await confirmationModal.onDidDismiss();
    this.isShowingDeleteConfirm.set(false);
  }

  async cancelDeleteConfirm(): Promise<void> {
    this.isShowingDeleteConfirm.set(false);
    this.modalCtrl.dismiss();
  }

  async confirmCloseCurrentSupportRequest(): Promise<void> {
    const session = this.currentSession();
    if (!session) {
      return;
    }
    await this.conciergeQueries.closeRequestMutation.mutateAsync(session.id);
    await this.conciergeQueries.refetchSessions();
    this.editingSubject.set(false);
    this.editingSubjectValue.set('');
    const sessions = this.sessions();
    if (sessions.length > 0) {
      this.conciergeQueries.selectSession(sessions[0].id);
    } else {
      this.createNewSession();
    }
    this.util.showSuccess('Chat deleted');
    this.modalCtrl.dismiss();
  }

  editSessionSubject(session: ChatSession): void {
    this.conciergeQueries.selectSession(session.id);
    this.isCreatingNewChat.set(false);
    this.localMessages.set([]);
    this.shouldScrollToBottom = true;
    this.startEditingSubject();
  }

  onSessionSelected(sessionValue: string): void {
    const session = this.sessions().find(s => s.id.toString() === sessionValue);
    if (session) {
      this.selectSession(session);
    }
  }

  onEditSessionItem(item: SelectModel): void {
    const session = this.sessions().find(s => s.id.toString() === item.value);
    if (session) {
      this.editSessionSubject(session);
    }
  }

  onDeleteSessionItem(item: SelectModel): void {
    const session = this.sessions().find(s => s.id.toString() === item.value);
    if (session) {
      this.deleteSession(session);
    }
  }

  // --- Gemini Live voice mode ---

  private async connectToGemini(): Promise<void> {
    this.waitingForGreeting = false;
    this.awaitingFirstUserSpeech.set(true);
    this.subscribeToGeminiEvents();
    await this.geminiLive.connect();
    this.sendGeminiInitialContext();
    await this.startGeminiMicStreaming();
    if (this.isVideoOn() && this.videoStream) {
      this.startGeminiVideoStreaming();
    }
  }

  private subscribeToGeminiEvents(): void {
    if (this.geminiEventsSubscribed) {return;}
    this.geminiEventsSubscribed = true;
    this.geminiLive.audioChunk$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(audio => {
      this.playGeminiAudioChunk(audio);
    });

    this.geminiLive.toolCall$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(toolCall => {
      this.handleGeminiToolCall(toolCall);
    });

    this.geminiLive.modelTranscription$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(chunk => {
      this.modelTranscript.update(t => t + chunk);
    });

    this.geminiLive.interrupted$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.modelTurnCutOff = true;
      this.modelTranscript.set('');
    });

    this.geminiLive.transcription$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(chunk => {
      if (this.modelTranscript()) {
        this.modelTurnCutOff = true;
      }
      this.modelTranscript.set('');
      this.liveTranscript.update(t => t + chunk);
      if (this.awaitingFirstUserSpeech()) {
        this.awaitingFirstUserSpeech.set(false);
      }
    });

    this.geminiLive.turnComplete$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.waitingForGreeting) {
        this.waitingForGreeting = false;
      }
      this.flushGeminiSpeech();
    });

    this.geminiLive.error$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(errorMsg => {
      this.util.showError(errorMsg);
      this.exitVoiceMode();
    });

    effect(() => {
      const reconnecting = this.geminiLive.isReconnecting();
      this.isGeminiReconnecting.set(reconnecting);
      if (reconnecting) {
        this.geminiAudioProcessor?.disconnect();
        this.geminiAnalyser?.disconnect();
        this.stopGeminiVideoStreaming();
      } else if (this.isVoiceMode()) {
        if (this.geminiAudioProcessor && this.geminiAudioContext && this.geminiMicStream) {
          const source = this.geminiAudioContext.createMediaStreamSource(this.geminiMicStream);
          if (this.geminiAnalyser) {
            source.connect(this.geminiAnalyser);
            this.geminiAnalyser.connect(this.geminiAudioProcessor);
          } else {
            source.connect(this.geminiAudioProcessor);
          }
          this.geminiAudioProcessor.connect(this.geminiAudioContext.destination);
        }
        if (this.isVideoOn() && this.videoStream) {
          this.startGeminiVideoStreaming();
        }
      }
    }, { injector: this.injector });
  }

  private sendGeminiTextMessage(text: string): void {
    this.geminiLive.sendText(text);
  }

  private async sendGeminiInitialContext(): Promise<void> {
    const firstName = localStorage.getItem('firstName') || '';
    const lastName = localStorage.getItem('lastName') || '';
    const email = localStorage.getItem('email') || '';
    const accountType = localStorage.getItem('accountType') || '';
    const isRentalClient = localStorage.getItem('isRentalClient') === 'true';

    const parts = [
      `User name: ${firstName} ${lastName}`.trim(),
      email ? `Email: ${email}` : '',
      `Account type: ${accountType}`,
      `Rental client: ${isRentalClient}`,
    ].filter(Boolean);

    try {
      const addresses = await this.client.getAddresses();
      if (addresses?.length > 0) {
        const addressList = addresses.map((a: any) =>
          `${this.client.buildAddressName(a)} (ID: ${a.id})`
        ).join(', ');
        parts.push(`Addresses: ${addressList}`);
      }
    } catch {
      // Failed to load addresses for context
    }

    // try {
    //   const categories = await this.genome.getCategories();
    //   if (categories?.length > 0) {
    //     const categoryList = categories.map((c: any) => `${c.name} (ID: ${c.id})`).join(', ');
    //     parts.push(`Asset categories: ${categoryList}`);
    //   }
    // } catch (err) {
    //   console.error('[Gemini] Failed to load asset categories for context:', err);
    // }

    const contextText = `[User context] ${parts.join('. ')}.`;
    this.geminiLive.sendContext(contextText);
  }

  private async getPreferredAudioInputDeviceId(): Promise<string | undefined> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      const localDevice = audioInputs.find(d => {
        const label = d.label.toLowerCase();
        return label && !label.includes('iphone') && !label.includes('bluetooth') && !label.includes('airpods');
      });
      return localDevice?.deviceId || undefined;
    } catch {
      return undefined;
    }
  }

  private async startGeminiMicStreaming(): Promise<void> {
    try {
      const baseConstraints: MediaTrackConstraints = {
        sampleRate: 16000,
        echoCancellation: true,
        noiseSuppression: true,
      };
      // Phase 1: Get temporary stream to trigger permission (populates device labels)
      let stream = await navigator.mediaDevices.getUserMedia({ audio: baseConstraints });

      // Phase 2: Now labels are available - find preferred local device
      const preferredDeviceId = await this.getPreferredAudioInputDeviceId();
      if (preferredDeviceId) {
        const currentDeviceId = stream.getAudioTracks()[0]?.getSettings()?.deviceId;
        if (currentDeviceId !== preferredDeviceId) {
          try {
            const exactStream = await navigator.mediaDevices.getUserMedia({
              audio: { ...baseConstraints, deviceId: { exact: preferredDeviceId } },
            });
            stream.getTracks().forEach(track => track.stop());
            stream = exactStream;
          } catch {
            // exact match failed - keep using the temporary stream
          }
        }
      }

      this.geminiMicStream = stream;
      this.geminiAudioContext = new AudioContext({ sampleRate: 16000 });
      if (this.geminiAudioContext.state === 'suspended') {
        await this.geminiAudioContext.resume();
      }
      const source = this.geminiAudioContext.createMediaStreamSource(this.geminiMicStream);
      this.geminiAudioProcessor = this.geminiAudioContext.createScriptProcessor(4096, 1, 1);
      this.geminiAnalyser = this.geminiAudioContext.createAnalyser();
      this.geminiAnalyser.fftSize = 256;

      this.geminiAudioProcessor.onaudioprocess = (e) => {
        if (!this.geminiLive.isConnected() || this.isMuted() || this.isAiSpeaking()) {
          return;
        }
        const float32 = e.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(float32.length);
        for (let i = 0; i < float32.length; i++) {
          const s = Math.max(-1, Math.min(1, float32[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        const base64 = btoa(String.fromCharCode(...new Uint8Array(pcm16.buffer)));
        this.geminiLive.sendAudio(base64);
      };

      source.connect(this.geminiAnalyser);
      this.geminiAnalyser.connect(this.geminiAudioProcessor);
      this.geminiAudioProcessor.connect(this.geminiAudioContext.destination);
      this.startVoiceVisualization();
    } catch {
      this.util.showError('Microphone access denied');
      this.exitVoiceMode();
    }
  }

  private sendGeminiSpeech(text: string): void {
    if (!this.geminiLive.isConnected()) {return;}
    this.geminiLive.sendText(`[CORE-AGENT] ${text}`);
  }

  private playGeminiAudioChunk(base64Audio: string): void {
    if (this.suppressAudio() || this.isAudioMuted() || this.awaitingFirstUserSpeech()) {
      console.log('[Voice Audio] BLOCKED — suppressAudio:', this.suppressAudio(), 'audioMuted:', this.isAudioMuted(), 'awaitingFirst:', this.awaitingFirstUserSpeech());
      return;
    }
    if (!this.geminiPlayerContext) {
      this.geminiPlayerContext = new AudioContext({ sampleRate: 24000 });
      this.geminiPlayTime = 0;
    }

    const binaryString = atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const pcm16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      float32[i] = pcm16[i] / 32768;
    }

    const audioBuffer = this.geminiPlayerContext.createBuffer(1, float32.length, 24000);
    audioBuffer.getChannelData(0).set(float32);
    const source = this.geminiPlayerContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.geminiPlayerContext.destination);

    const now = this.geminiPlayerContext.currentTime;
    const startTime = Math.max(now, this.geminiPlayTime);
    source.start(startTime);
    this.geminiPlayTime = startTime + audioBuffer.duration;

    this.isAiSpeaking.set(true);
    this.geminiPendingAudioSources.push(source);
    source.onended = () => {
      this.geminiPendingAudioSources = this.geminiPendingAudioSources.filter(s => s !== source);
      if (this.geminiPendingAudioSources.length === 0) {
        this.isAiSpeaking.set(false);
      }
    };
  }

  private startGeminiVideoStreaming(): void {
    const videoEl = this.voiceVideoElement?.nativeElement;
    if (!videoEl || !videoEl.srcObject) {return;}

    this.geminiVideoCanvas = document.createElement('canvas');
    this.geminiVideoCanvas.width = 1280;
    this.geminiVideoCanvas.height = 720;

    this.geminiLive.sendContext('[Video mode enabled] The user is showing you their home through the camera. Wait for the user to point the camera at something and ask you to identify it. Do NOT proactively suggest or add assets — only use the add_asset tool when you are highly confident you can clearly see a specific appliance or asset in the frame AND the user has asked you to add it. If you are unsure, ask the user what they are showing you instead of guessing.');

    this.prevFrameData = null;
    this.lastAutoScanTime = Date.now();

    this.geminiVideoIntervalId = setInterval(() => {
      if (!this.geminiLive.isConnected()) {
        return;
      }

      const ctx = this.geminiVideoCanvas.getContext('2d');
      ctx.drawImage(videoEl, 0, 0, 1280, 720);

      const currFrame = ctx.getImageData(0, 0, 1280, 720);
      if (this.prevFrameData) {
        const diff = this.computeFrameDiff(this.prevFrameData, currFrame.data);
        const now = Date.now();
        const AUTO_SCAN_COOLDOWN = 5000;
        const SCENE_CHANGE_THRESHOLD = 0.16;
        if (diff > SCENE_CHANGE_THRESHOLD && now - this.lastAutoScanTime > AUTO_SCAN_COOLDOWN) {
          this.lastAutoScanTime = now;
          this.scanCurrentFrame();
        }
      }
      this.prevFrameData = currFrame.data;

      this.geminiVideoCanvas.toBlob((blob) => {
        if (!blob) {
          return;
        }
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          this.geminiLive.sendVideo(base64);
        };
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 1.0);
    }, 1000);
  }

  private stopGeminiVideoStreaming(): void {
    if (this.geminiVideoIntervalId) {
      clearInterval(this.geminiVideoIntervalId);
      this.geminiVideoIntervalId = null;
    }
    this.prevFrameData = null;
  }

  private computeFrameDiff(prev: Uint8ClampedArray, curr: Uint8ClampedArray): number {
    let totalDiff = 0;
    const step = 16;
    let samples = 0;
    for (let i = 0; i < prev.length; i += step * 4) {
      totalDiff += Math.abs(prev[i] - curr[i])
        + Math.abs(prev[i + 1] - curr[i + 1])
        + Math.abs(prev[i + 2] - curr[i + 2]);
      samples++;
    }
    return totalDiff / (samples * 3 * 255);
  }

  private readonly pendingLookup = signal<{
    toolCallId: string;
    conciergeItemId: number;
    firstNewAiMsgIndex: number;
    timeoutHandle: ReturnType<typeof setTimeout>;
    resolved: boolean;
  } | null>(null);
  private readonly LOOKUP_TIMEOUT_MS = 60_000;
  private suppressNextFlush = false;
  // The current model turn lost its start or its end (the user talked over it), so its
  // transcript is a fragment and must not be posted to the ticket.
  private modelTurnCutOff = false;
  private lastPostedVoiceLine: string | null = null;

  private sendToolResponse(toolCallId: string, toolName: string, text: string): void {
    console.log('[Voice SendToolResponse]', toolName, '→', text.substring(0, 80));
    this.geminiLive.sendToolResponse(toolCallId, toolName, text);
  }

  private stripUrlsForVoice(text: string): string {
    return text.replace(/https?:\/\/[^\s)]+/g, '').replace(/\s{2,}/g, ' ').trim();
  }

  private startThinkingSpinner(): void {
    this.stopThinkingSpinner();
    const spinner = this.ASCII_SPINNERS[Math.floor(Math.random() * this.ASCII_SPINNERS.length)];
    let frameIndex = 0;
    this.thinkingSpinner.set(spinner.frames[0]);
    this.thinkingIntervalId = setInterval(() => {
      frameIndex = (frameIndex + 1) % spinner.frames.length;
      this.thinkingSpinner.set(spinner.frames[frameIndex]);
    }, spinner.interval);
  }

  private stopThinkingSpinner(): void {
    if (this.thinkingIntervalId) {
      clearInterval(this.thinkingIntervalId);
      this.thinkingIntervalId = null;
    }
    this.thinkingSpinner.set('');
  }

  private async handleGeminiToolCall(toolCall: any): Promise<void> {
    const fcNames = toolCall?.functionCalls?.map((fc: any) => fc.name) || [];
    console.log('[Voice ToolCall] received:', fcNames.join(', '));
    const result = await this.geminiToolHandler.handleToolCall(toolCall);

    if (result.uiAction === 'prompt_password_change') {
      this.pendingPasswordToolCallId = result.uiActionToolCallId;
      this.isPasswordOverlayVisible.set(true);
    }

    if (result.uiAction === 'send_transcript') {
      const toolCallId = result.uiActionToolCallId!;
      const args = result.uiActionData || {};
      const transcript: string = (args.transcript || '').trim();

      const systemPromptPattern = /^\[(?:User context|Video mode enabled|Scan requested|Wrong detection|CORE-AGENT|CONCIERGE_RESPONSE|LOOKUP_TIMEOUT|ESCALATED|POSTED)/;
      if (!transcript || systemPromptPattern.test(transcript)) {
        // Guard: Gemini must not echo our own injected markers back as transcripts.
        this.geminiLive.sendToolResponse(toolCallId, 'send_transcript', '[POSTED]');
        return;
      }

      // Ack Gemini immediately so it is not blocked on the core-agent lookup.
      this.geminiLive.sendToolResponse(toolCallId, 'send_transcript', '[POSTED]');

      this.awaitingFirstUserSpeech.set(false);

      const userMessage: ChatMessage = {
        id: Date.now(),
        role: 'user',
        content: transcript,
        timestamp: new Date().toISOString(),
        attachments: [],
      };
      this.localMessages.update(msgs => [...msgs, userMessage]);
      this.shouldScrollToBottom = true;
      this.liveTranscript.set('');

      this.startLookup(toolCallId, transcript, transcript);
      return;
    }

    if (result.uiAction === 'log_transcript') {
      const toolCallId = result.uiActionToolCallId!;
      const args = result.uiActionData || {};
      const transcript: string = (args.transcript || '').trim();
      console.log('[Voice log_transcript] Gemini called log_transcript →', transcript.substring(0, 80));

      const systemPromptPattern = /^\[(?:User context|Video mode enabled|Scan requested|Wrong detection|CORE-AGENT|CONCIERGE_RESPONSE|LOOKUP_TIMEOUT|ESCALATED|POSTED|LOGGED)/;
      if (!transcript || systemPromptPattern.test(transcript)) {
        this.geminiLive.sendToolResponse(toolCallId, 'log_transcript', '[LOGGED]');
        return;
      }

      // Ack Gemini immediately — log_transcript never blocks.
      this.geminiLive.sendToolResponse(toolCallId, 'log_transcript', '[LOGGED]');

      this.awaitingFirstUserSpeech.set(false);

      const userMessage: ChatMessage = {
        id: Date.now(),
        role: 'user',
        content: transcript,
        timestamp: new Date().toISOString(),
        attachments: [],
      };
      this.localMessages.update(msgs => [...msgs, userMessage]);
      this.shouldScrollToBottom = true;
      this.liveTranscript.set('');

      // Fire-and-forget backend record; skip if no concierge item exists yet.
      // Future BE work: see .context/future-work.md — a non-processing create
      // endpoint would let us log chit-chat that happens before any send_transcript.
      const session = this.currentSession();
      if (session?.id) {
        this.conciergeApi.recordUserComment(session.id, transcript).subscribe({
          error: () => { /* silent */ },
        });
      }
      return;
    }

    if (result.uiAction === 'get_conversation_history') {
      const toolCallId = result.uiActionToolCallId;
      const systemPromptPattern = /^\[(?:User context|Video mode enabled|Scan requested|Wrong detection|CORE-AGENT)/;
      const conversationMessages = this.messages().filter(m => !systemPromptPattern.test(m.content));
      if (conversationMessages.length > 0) {
        const history = conversationMessages.map(m => {
          const sender = m.role === 'user' ? 'User' : 'Core-Agent';
          return `${sender}: ${m.content}`;
        }).join('\n');
        this.sendToolResponse(toolCallId, 'get_conversation_history', history);
      } else {
        this.sendToolResponse(toolCallId, 'get_conversation_history', 'No prior conversation history.');
      }
    }

    if (result.uiAction === 'confirm_add_asset') {
      const toolCallId = result.uiActionToolCallId;
      const args = result.uiActionData;

      // Immediately respond so Gemini keeps scanning
      this.geminiLive.sendToolResponse(toolCallId, 'add_asset', 'Suggestion shown to user for confirmation. Continue scanning for more assets.');

      const resolvedCategoryId = await this.geminiToolHandler.resolveCategoryId(args?.name, args?.category);
      let categoryName = args?.name || 'Unknown asset';
      let addressName = '';
      let iconUrl = '';
      try {
        if (resolvedCategoryId) {
          const categories = await this.genome.getCategories();
          const category = categories?.find((c: any) => c.id === resolvedCategoryId);
          if (category) {
            categoryName = category.name;
            iconUrl = category.default_icon_url || '';
          }
        }
        const addresses = await this.client.getAddresses();
        const address = addresses?.find((a: any) => a.id === args?.address_id);
        if (address) { addressName = this.client.buildAddressName(address); }
      } catch {
        // Failed to resolve asset display names
      }
      const enrichedArgs = { ...args, category_id: resolvedCategoryId };
      const corner = this.nextAssetCorner % 4;
      this.nextAssetCorner++;
      this.pendingAssets.update(list => [...list, { id: toolCallId, categoryName, addressName, iconUrl, args: enrichedArgs, corner }]);
    }

    if (result.functionResponses.length > 0) {
      for (const fr of result.functionResponses) {
        this.geminiLive.sendToolResponse(fr.id, fr.name, fr.response.result);
      }
    }
  }

  private async startLookup(toolCallId: string, transcript: string, backendContent: string): Promise<void> {
    // If the user course-corrects mid-lookup, mark the prior lookup as superseded.
    // We don't push anything to Gemini — the new send_transcript call already
    // told Gemini we're starting fresh. Just stop the old watchdog.
    const prior = this.pendingLookup();
    if (prior && !prior.resolved) {
      clearTimeout(prior.timeoutHandle);
      prior.resolved = true;
    }

    const startingMessages = this.conciergeQueries.selectedMessages();
    const firstNewAiMsgIndex = startingMessages.length;

    const lookup = {
      toolCallId,
      conciergeItemId: 0,
      firstNewAiMsgIndex,
      timeoutHandle: undefined as any,
      resolved: false,
    };
    lookup.timeoutHandle = setTimeout(() => {
      if (!lookup.resolved) {
        this.finalizeLookup(lookup, '[LOOKUP_TIMEOUT]');
      }
    }, this.LOOKUP_TIMEOUT_MS);
    this.pendingLookup.set(lookup);

    this.startThinkingSpinner();
    this.pendingIndicatorLastMsgId = startingMessages.length > 0
      ? startingMessages[startingMessages.length - 1].id
      : null;
    this.showPendingIndicator.set(true);

    const session = this.currentSession();
    try {
      if (!session?.supportTicketId && this.isCreatingNewChat()) {
        const subject = await this.summarizeSubject(transcript);
        const item = {
          data: { subject, body: backendContent, attachments: [], emails: [] },
          type: 'support.other',
        };
        const response = await this.conciergeQueries.createItemMutation.mutateAsync(item);
        await this.conciergeQueries.refetchSessions();
        this.conciergeQueries.selectSession(response.id);
        this.isCreatingNewChat.set(false);
        this.geminiToolHandler.setVoiceSessionTicketId(response.metadata?.support_ticket_id || null);
        lookup.conciergeItemId = response.id;
        this.pendingLookup.set({ ...lookup });
      } else if (session?.supportTicketId) {
        await this.conciergeQueries.addCommentMutation.mutateAsync({
          support_ticket_id: session.supportTicketId,
          content: backendContent,
          attachments: [],
        });
        lookup.conciergeItemId = session.id;
        this.pendingLookup.set({ ...lookup });
      } else {
        this.finalizeLookup(lookup, '[LOOKUP_TIMEOUT]');
      }
    } catch {
      this.finalizeLookup(lookup, '[LOOKUP_TIMEOUT]');
    }
  }

  private finalizeLookup(lookup: {
    toolCallId: string;
    conciergeItemId: number;
    firstNewAiMsgIndex: number;
    timeoutHandle: ReturnType<typeof setTimeout>;
    resolved: boolean;
  }, payload: string): void {
    if (lookup.resolved) {return;}
    lookup.resolved = true;
    clearTimeout(lookup.timeoutHandle);

    this.stopThinkingSpinner();
    this.clearPendingIndicator();
    this.shouldScrollToBottom = true;

    // Inject the marker as a user turn. Gemini's prompt tells it to treat
    // [CONCIERGE_RESPONSE: ...], [LOOKUP_TIMEOUT], [ESCALATED] as system-pushed
    // context and speak the answer (or stay silent for ESCALATED).
    // The turn is held while Gemini finishes its filler, so suppress the flush only once it goes
    // out: the filler before it still belongs on the ticket.
    this.geminiLive.sendText(payload, () => {
      if (payload.startsWith('[CONCIERGE_RESPONSE')) {
        this.suppressNextFlush = true;
      }
    });

    if (this.pendingLookup()?.toolCallId === lookup.toolCallId) {
      this.pendingLookup.set(null);
    }
  }

  private sendPasswordToolResponse(result: string): void {
    if (!this.pendingPasswordToolCallId || !this.geminiLive.isConnected()) {
      return;
    }
    this.geminiLive.sendToolResponse(this.pendingPasswordToolCallId, 'prompt_password_change', result);
    this.pendingPasswordToolCallId = null;
  }

  async submitPassword(): Promise<void> {
    const password = this.passwordForm.get('password')?.value;
    const confirmPassword = this.passwordForm.get('confirmPassword')?.value;

    if (!password || !confirmPassword) {
      this.passwordError.set('Please fill in both fields.');
      return;
    }
    if (password !== confirmPassword) {
      this.passwordError.set('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      this.passwordError.set('Password must be at least 6 characters.');
      return;
    }

    try {
      await this.geminiToolHandler.changePassword(password);
      this.isPasswordOverlayVisible.set(false);
      this.passwordForm.reset();
      this.passwordError.set('');
      this.sendPasswordToolResponse('Password set successfully. Please inform the user.');
    } catch (err: any) {
      this.passwordError.set(err?.error?.message || 'Failed to set password.');
    }
  }

  cancelPasswordOverlay(): void {
    this.isPasswordOverlayVisible.set(false);
    this.passwordForm.reset();
    this.passwordError.set('');
    this.sendPasswordToolResponse('User cancelled password setting.');
  }

  async confirmAddAsset(asset: { id: string; args: any }): Promise<void> {
    this.removeAssetCard(asset.id);
    try {
      await this.geminiToolHandler.addAsset(asset.args);
    } catch {
      this.util.showError('Failed to add asset');
    }
  }

  cancelAddAsset(asset: { id: string }): void {
    this.removeAssetCard(asset.id);
  }

  scanCurrentFrame(): void {
    if (!this.geminiLive.isConnected()) {return;}
    this.isScanning.set(true);
    setTimeout(() => this.isScanning.set(false), 1500);
    const videoEl = this.voiceVideoElement?.nativeElement;
    if (!videoEl || !this.geminiVideoCanvas) {
      return;
    }
    const ctx = this.geminiVideoCanvas.getContext('2d');
    ctx.drawImage(videoEl, 0, 0, 1280, 720);
    this.geminiVideoCanvas.toBlob((blob) => {
      if (!blob) { return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = (reader.result as string).split(',')[1];
        this.geminiLive.sendVideo(base64);
        this.sendGeminiTextMessage('[Scan requested] The user pressed the Scan button. Carefully analyze THIS specific frame and identify any home assets or appliances you can clearly see. Use the add_asset tool for each one you are confident about. If you don\'t see any recognizable assets, say so.');
      };
      reader.readAsDataURL(blob);
    }, 'image/jpeg', 1.0);
  }

  rejectAddAsset(asset: { id: string; categoryName: string }): void {
    this.removeAssetCard(asset.id);
    if (this.geminiLive.isConnected()) {
      this.sendGeminiTextMessage(`[Wrong detection] The object you identified as "${asset.categoryName}" is incorrect. Please look at the current frame again more carefully and try to identify what is actually there.`);
    }
  }

  private removeAssetCard(id: string): void {
    this.pendingAssets.update(list => list.filter(a => a.id !== id));
  }

  private flushGeminiSpeech(): void {
    const raw = this.modelTranscript().trim();
    const cutOff = this.modelTurnCutOff;
    this.modelTurnCutOff = false;
    if (!raw) {return;}
    this.modelTranscript.set('');

    if (this.suppressNextFlush) {
      this.suppressNextFlush = false;
      return;
    }
    // A cut-off turn is a fragment, and after a handoff the audio is muted so the user never
    // heard it. Neither belongs on the ticket.
    if (cutOff || this.suppressAudio()) {return;}

    const text = cleanVoiceTranscript(raw, this.lastPostedVoiceLine);
    if (!text) {return;}
    this.lastPostedVoiceLine = text;

    const aiMessage: ChatMessage = {
      id: Date.now(),
      role: 'ai',
      content: text,
      timestamp: new Date().toISOString(),
      attachments: [],
      userType: 'AiFrontendAgent',
    };
    this.localMessages.update(msgs => [...msgs, aiMessage]);
    this.shouldScrollToBottom = true;

    const session = this.currentSession();
    if (session?.supportTicketId) {
      this.conciergeQueries.addCommentMutation.mutateAsync({
        support_ticket_id: session.supportTicketId,
        content: text,
        attachments: [],
        is_ai_response: true,
      }).catch(() => { /* silent */ });
    }
  }

  private cleanupVoiceMode(): void {
    const current = this.pendingLookup();
    if (current && !current.resolved) {
      clearTimeout(current.timeoutHandle);
      current.resolved = true;
    }
    this.pendingLookup.set(null);
    this.stopVoiceVisualization();
    this.stopGeminiVideoStreaming();

    this.geminiAnalyser?.disconnect();
    this.geminiAnalyser = null;
    this.geminiAudioProcessor?.disconnect();
    this.geminiAudioProcessor = null;

    if (this.geminiAudioContext) {
      this.geminiAudioContext.close();
      this.geminiAudioContext = null;
    }

    if (this.geminiPlayerContext) {
      this.geminiPlayerContext.close();
      this.geminiPlayerContext = null;
    }
    this.geminiPendingAudioSources = [];
    this.geminiPlayTime = 0;

    if (this.geminiMicStream) {
      this.geminiMicStream.getTracks().forEach(track => track.stop());
      this.geminiMicStream = null;
    }

    this.geminiLive.disconnect();

    this.geminiToolHandler.setVoiceSessionTicketId(null);
    this.liveTranscript.set('');
    this.modelTranscript.set('');
    this.suppressAudio.set(false);
    this.isAudioMuted.set(false);
    this.awaitingFirstUserSpeech.set(false);
    this.suppressNextFlush = false;
    this.modelTurnCutOff = false;
    this.lastPostedVoiceLine = null;
    this.stopThinkingSpinner();
  }

  private startVoiceVisualization(): void {
    if (this.voiceAnimationFrameId !== null) {return;}

    this.ngZone.runOutsideAngular(() => {
      const frequencyData = new Uint8Array(this.geminiAnalyser!.frequencyBinCount);
      const barCount = 11;
      const mid = Math.floor(barCount / 2);

      const animate = () => {
        this.voiceAnimationFrameId = requestAnimationFrame(animate);
        if (!this.geminiAnalyser) {return;}

        this.geminiAnalyser.getByteFrequencyData(frequencyData);

        let sum = 0;
        for (const value of frequencyData) {
          sum += value;
        }
        const rawVolume = sum / (frequencyData.length * 255);
        this.smoothedVolume = this.smoothedVolume * 0.85 + rawVolume * 0.15;
        const vol = this.smoothedVolume;

        const barsEl = this.soundBarsContainer?.nativeElement;
        if (barsEl) {
          const bars = barsEl.children;
          for (let i = 0; i < bars.length && i < barCount; i++) {
            const distFromCenter = Math.abs(mid - i);
            const freqIndex = Math.floor((i / barCount) * (frequencyData.length / 2));
            const rawVal = frequencyData[freqIndex] / 255;
            const height = 3 + (rawVal * 18) + (vol * 10 * (1 - distFromCenter / mid));
            (bars[i] as HTMLElement).style.height = `${Math.min(28, Math.max(3, height))}px`;
            (bars[i] as HTMLElement).style.opacity = `${0.5 + rawVal * 0.5}`;
          }
        }

        const orbEl = this.voiceOrb?.nativeElement;
        if (orbEl) {
          orbEl.style.setProperty('--vol', vol.toString());
          const scale = 1 + vol * 0.6;
          const glowSpread = 30 + vol * 50;
          const glowOpacity = 0.4 + vol * 0.6;
          orbEl.style.transform = `scale(${scale})`;
          orbEl.style.boxShadow = `0 0 ${glowSpread}px 5px rgba(0, 170, 186, ${glowOpacity})`;
        }
      };

      animate();
    });
  }

  private stopVoiceVisualization(): void {
    if (this.voiceAnimationFrameId !== null) {
      cancelAnimationFrame(this.voiceAnimationFrameId);
      this.voiceAnimationFrameId = null;
    }
    this.smoothedVolume = 0;
  }

  async deleteSession(session: ChatSession): Promise<void> {
    this.isShowingDeleteConfirm.set(true);
    const alert = await this.util.showConfirmAlert({
      header: 'Delete Support Request',
      message:
        'Are you sure you want to delete this support request? This action cannot be undone.',
    });
    const { role } = await alert.onDidDismiss();
    this.isShowingDeleteConfirm.set(false);
    if (role !== 'confirm') {
      return;
    }
    try {
      const currentId = this.currentSessionId();
      await this.conciergeQueries.closeRequestMutation.mutateAsync(session.id);
      if (currentId === session.id) {
        await this.conciergeQueries.refetchSessions();
        const sessions = this.sessions();
        if (sessions.length > 0) {
          this.conciergeQueries.selectSession(sessions[0].id);
        } else {
          this.createNewSession();
        }
      }
      this.util.showSuccess('Support request deleted');
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to delete support request');
    }
  }

  // The dispute banner only shows when the open thread is itself a dispute
  // concierge item, not whenever a dispute exists somewhere for the customer.
  readonly isDisputeThread = this.conciergeQueries.isDisputeThread;

  async goToDisputeResolution(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.conciergeApi.getConciergeItems('&is_marked_done=false&per_page=20')
      );
      const dispute = response.body.find(item => item.type?.startsWith('dispute.'));
      if (!dispute) {
        this.util.showError('No pending dispute found.');
        return;
      }
      const params = { dispute, backPage: 'notification-center' };
      this.rightSidePanelService.navigateTo(
        `dispute-resolution/${dispute.id}`, params, DisputeResolutionPage
      );
    } catch (err: any) {
      this.util.showError(err?.error?.message || err?.message || 'Failed to load dispute.');
    }
  }

  private async summarizeSubject(message: string): Promise<string> {
    try {
      const response = await this.llmService.suggest({
        systemPrompt: 'You are a helpful assistant that generates concise chat subjects. Given a user message, produce a short subject line (max 8 words) that captures the main topic. Return only the subject text, no quotes or extra formatting.',
        prompt: message,
        responseSchema: {
          type: 'object',
          properties: {
            subject: { type: 'string' }
          },
          required: ['subject']
        },
        maxTokens: 50
      });
      return response.result?.subject || message.substring(0, 100);
    } catch {
      return message.substring(0, 100);
    }
  }
}
